import { useState } from 'react';

import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import * as Yup from 'yup';

import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField, TextField } from '../../../components/inputs';
import { Modal } from '../../../components/Modal';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useApprovePostRequestOverride } from '../../../lib/post/hooks/useApprovePostRequestOverride';
import useGetAllPostRequests from '../../../lib/post/hooks/useGetAllPostRequests';
import { useRejectPostRequestOverride } from '../../../lib/post/hooks/useRejectPostRequestOverride';
import { SortDirection } from '../../../lib/util/helpers';
import { sortToObj } from '../../../lib/util/sortToObj';
import { PostRequest, PostRequestStatus } from '../../../types/PostRequest';

type RejectPostRequestOverrideFormType = {
  rejectedReason: string;
};

const RejectPostRequestOverrideSchema = Yup.object().shape({
  rejectedReason: Yup.string().required('Rejection reason is required'),
});

function PostRequestOverrideList() {
  const navigate = useNavigate();
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
    type: StringParam,
  });
  const { page, search, sort, type } = query;

  const [rejectedReasonModalIsOpen, setRejectedReasonModalIsOpen] = useState(false);
  const [selectedPostRequestId, setSelectedPostRequestId] = useState<string>('');
  const searchString = search && { search };
  const sortObj = sortToObj(sort ?? '');
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const setNewPage = (page: number) => setQuery({ page: page - 1 });
  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const typeString = type && { type };

  const {
    data: paginatedPostRequests,
    isLoading,
    error,
  } = useGetAllPostRequests({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
    ...typeString,
  });

  const { mutate: approvePostRequestOverride, isLoading: isApproving } = useApprovePostRequestOverride();
  const { mutate: rejectPostRequestOverride, isLoading: isRejecting } = useRejectPostRequestOverride();
  const initialValues: RejectPostRequestOverrideFormType = { rejectedReason: '' };

  return (
    <>
      <Container>
        <FullWidthBar>
          <Typography variant="h5" sx={{ marginRight: '20px' }}>
            Article Post Request Overrides
          </Typography>
          <RightControls>
            <FormControl size="small" sx={{ minWidth: 160, marginRight: 2 }}>
              <InputLabel id="type-filter-label">Type</InputLabel>
              <Select
                labelId="type-filter-label"
                id="type-filter"
                value={type ?? ''}
                label="Type"
                onChange={(e) => setQuery({ type: (e.target.value as string) || undefined, page: 0 })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="edit">Edit</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
              </Select>
            </FormControl>
            <StyledSearchField
              searchString={search ?? ''}
              onSearch={(search: string) => {
                debouncedSearchFn(search);
              }}
            />
          </RightControls>
        </FullWidthBar>

        <TableContainer sx={{ backgroundColor: 'white' }}>
          <Table aria-labelledby="tableTitle">
            <TableHead>
              <PointerTableRow>
                {columns.map((column) => (
                  <TableCell key={column.name}>
                    <TableSortLabel
                      active={sortObj.column === column.name}
                      direction={sortObj.direction}
                      onClick={() => {
                        const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                        setNewSort(column.name, isAsc ? 'desc' : 'asc');
                      }}
                      component="a"
                    >
                      {column.text}
                      {sortObj.column === column.name ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </PointerTableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Spinner />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !error &&
                paginatedPostRequests?.data &&
                paginatedPostRequests.data.map((row: PostRequest, index: number) => {
                  const approvedDate =
                    row.status === PostRequestStatus.APPROVED ? row.approved ?? row.modified : null;

                  return (
                    <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                      <TitleTableCell>{row.post.title}</TitleTableCell>
                      <TableCell>{row.post.category?.parent?.title ?? row.post.category?.title}</TableCell>
                      <TableCell>{row.post.author.username}</TableCell>
                      <TableCell>{new Date(row.created).toLocaleDateString()}</TableCell>
                      <TableCell>{approvedDate ? new Date(approvedDate).toLocaleString() : '—'}</TableCell>
                      <TableCell>{row.reason}</TableCell>
                      <TableCell>
                        {row.type ? (
                          row.type === 'edit' ? (
                            <Chip label="Edit" color="info" size="small" variant="outlined" />
                          ) : (
                            <Chip label="Delete" color="error" size="small" variant="outlined" />
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {row.status === PostRequestStatus.APPROVED ? (
                          <Chip label="Approved" color="success" variant="outlined" />
                        ) : row.status === PostRequestStatus.REJECTED ? (
                          <Chip label="Rejected" color="error" variant="outlined" />
                        ) : (
                          <Chip label="Pending" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <ConfirmDialog
                          trigger={(open) => (
                            <Button
                              onClick={open}
                              type="button"
                              style={{ textTransform: 'capitalize' }}
                              disabled={row.status !== PostRequestStatus.PENDING}
                            >
                              Approve
                            </Button>
                          )}
                          title="Approve post edit request"
                          description="Are you sure you want to approve this post edit request?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Approve'}
                          onConfirm={async (close) => {
                            await approvePostRequestOverride({ requestId: row.id });
                            close();
                          }}
                          disabled={isApproving}
                          loading={isApproving}
                        />

                        <ConfirmDialog
                          trigger={(open) => (
                            <Button
                              onClick={open}
                              type="button"
                              style={{ textTransform: 'capitalize' }}
                              disabled={row.status !== PostRequestStatus.PENDING}
                            >
                              Reject
                            </Button>
                          )}
                          title="Reject post edit request"
                          description="Are you sure you want to reject this post edit request?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Submit'}
                          onConfirm={async (close) => {
                            setSelectedPostRequestId(row.id);
                            setRejectedReasonModalIsOpen(true);
                            close();
                          }}
                          disabled={isRejecting}
                          loading={isRejecting}
                        />

                        <Button
                          onClick={() => navigate(`/cms/request-overrides/${row.id}`)}
                          type="button"
                          style={{ textTransform: 'capitalize' }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              {paginatedPostRequests?.data.length === 0 && (
                <StyledTableRow>
                  <TableCell colSpan={9} style={{ textAlign: 'center' }}>
                    No post request overrides found.
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginatedPostRequests?.total !== null && paginatedPostRequests?.total !== 0 && (
          <FullWidthBar>
            <Typography variant="subtitle1">Total: {paginatedPostRequests?.total}</Typography>
            <StyledPagination
              current={Number(page) + 1 || 1}
              total={Math.ceil((paginatedPostRequests?.total ?? 0) / Defaults.LIST_PAGE_SIZE)}
              onPageChange={(page: number) => setNewPage(page)}
            />
          </FullWidthBar>
        )}
      </Container>

      <Modal
        uniqueId="add-rejected-reason"
        isOpen={rejectedReasonModalIsOpen}
        handleClose={() => {
          setRejectedReasonModalIsOpen(false);
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={RejectPostRequestOverrideSchema}
          validateOnBlur
          onSubmit={(values) => {
            rejectPostRequestOverride({ requestId: selectedPostRequestId, dto: { rejectedReason: values.rejectedReason } });
            setRejectedReasonModalIsOpen(false);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '300px' }}>
              <Typography variant="h6">Reject Post Request Override</Typography>
              <TextField
                label="Rejection Reason"
                name="rejectedReason"
                multiline
                minRows={4}
                value={values.rejectedReason}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.rejectedReason && Boolean(errors.rejectedReason)}
                helperText={touched.rejectedReason && errors.rejectedReason}
                fullWidth
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button type="button" variant="outlined" onClick={() => setRejectedReasonModalIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isRejecting}>
                  Submit
                </Button>
              </Box>
            </Box>
          )}
        </Formik>
      </Modal>
    </>
  );
}

export default PostRequestOverrideList;

const columns = [
  {
    text: 'Article Title',
    name: 'post.title',
  },
  {
    text: 'Category',
    name: 'category.title',
  },
  {
    text: 'Author',
    name: 'author.username',
  },
  {
    text: 'Submitted Date',
    name: 'post_request.created',
  },
  {
    text: 'Approved Date',
    name: 'post_request.approved',
  },
  {
    text: 'Request Reason',
    name: 'post_request.reason',
  },
  {
    text: 'Request Type',
    name: 'post_request.type',
  },
  {
    text: 'Status',
    name: 'post_request.status',
  },
  {
    text: 'Actions',
    name: '',
  },
];

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const RightControls = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
});

const StyledSearchField = styled(SearchField)({
  // no margin - it will be aligned inside RightControls
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const TitleTableCell = styled(TableCell)({
  width: '280px',
  maxWidth: '280px',
  overflowWrap: 'anywhere',
});

const StyledTableRow = styled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});
